import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { InstructionsTextBlock } from 'components/flink-play';
import { toggleAudio } from 'actions/audioActions';
import { getInstruction } from 'activity-templates/utils';
import { withGetStringAudio } from 'components/hocs';
import { activitiesURL } from 'config';
import { getTranslateFunc } from 'utils';
import { inspectReadingString, logReadingLocale, isReadingLocaleDebugEnabled } from 'utils/readingLocaleDiagnostics';

class Instructions extends Component {
  state = {};

  componentDidMount() {
    this.setCurrentInstruction();

    document.addEventListener(
      'playInstructionsAudio',
      this.playInstructionsAudio
    );
  }

  componentDidUpdate(prevProps) {
    const {
      problem,
      solutionLocale,
      solutionLocaleOverride,
      instructionTextLocale,
      locale,
      textPath = 'instructionText',
      audioPath = 'instructionAudio',
      stringPath = 'instructionString',
    } = this.props;
    const prevTextPath = prevProps.textPath === undefined
      ? 'instructionText'
      : prevProps.textPath;
    const prevAudioPath = prevProps.audioPath === undefined
      ? 'instructionAudio'
      : prevProps.audioPath;
    const prevStringPath = prevProps.stringPath === undefined
      ? 'instructionString'
      : prevProps.stringPath;

    const prevProblem = prevProps.problem;
    const problemInstructionChanged =
      prevProblem !== problem &&
      (
        !prevProblem ||
        !problem ||
        prevProblem.id !== problem.id ||
        prevProblem._id !== problem._id ||
        prevProblem[prevTextPath] !== problem[textPath] ||
        prevProblem[prevAudioPath] !== problem[audioPath] ||
        prevProblem[prevStringPath] !== problem[stringPath] ||
        prevProblem.multiLocaleInstructions !== problem.multiLocaleInstructions
      );

    const prevEffectiveSolutionLocale =
      prevProps.solutionLocaleOverride !== undefined
        ? prevProps.solutionLocaleOverride
        : prevProps.solutionLocale;
    const effectiveSolutionLocale =
      solutionLocaleOverride !== undefined
        ? solutionLocaleOverride
        : solutionLocale;
    const prevSolutionLocaleCode = prevEffectiveSolutionLocale?.code || '';
    const solutionLocaleCode = effectiveSolutionLocale?.code || '';

    if (
      problemInstructionChanged ||
      prevSolutionLocaleCode !== solutionLocaleCode ||
      prevProps.instructionTextLocale !== instructionTextLocale ||
      prevProps.locale !== locale ||
      prevTextPath !== textPath ||
      prevAudioPath !== audioPath ||
      prevStringPath !== stringPath
    ) {
      this.setCurrentInstruction();
    }
  }

  componentWillUnmount() {
    document.removeEventListener(
      'playInstructionsAudio',
      this.playInstructionsAudio
    );
  }

  setCurrentInstruction = () => {
    const {
      problem,
      solutionLocale,
      solutionLocaleOverride,
      instructionTextLocale,
      strings,
      textPath,
      audioPath,
      stringPath,
      translate,
      getStringAudio,
      autoPlay
    } = this.props;

    const effectiveSolutionLocale = solutionLocaleOverride !== undefined
      ? solutionLocaleOverride
      : solutionLocale;

    // A numbered String uses its own translator, independently of audio.
    // Only Reading Deluxe passes this opt-in override.
    const instructionTranslate = instructionTextLocale && Array.isArray(strings)
      ? getTranslateFunc(strings, instructionTextLocale)
      : translate;

    const instruction = getInstruction(
      problem,
      effectiveSolutionLocale,
      textPath,
      audioPath,
      stringPath,
      { translate: instructionTranslate, getStringAudio },
    );

    if (this.props.readingLocaleDiagnostics && isReadingLocaleDebugEnabled()) {
      const textLocale = instructionTextLocale || null;
      const effectiveLocale = effectiveSolutionLocale?.code || null;
      const stringId = problem?.[stringPath || 'instructionString'];
      logReadingLocale('instruction-render', {
        activityId: this.props.activityId,
        pageId: problem?.id || problem?._id || null,
        pathKeys: {
          stringPath: stringPath || 'instructionString',
          textPath: textPath || 'instructionText',
          audioPath: audioPath || 'instructionAudio',
        },
        reduxLocale: solutionLocale?.code || null,
        solutionLocaleOverride: solutionLocaleOverride?.code || null,
        effectiveLocale,
        stringTextLocaleOverride: textLocale,
        chosenTextLocale: textLocale || 'redux solutionTranslate',
        chosenTextSource: stringId
          ? 'common.strings by configured instructionString'
          : problem?.multiLocaleInstructions ? 'multiLocaleInstructions' : 'instructionText',
        configuredProblemFields: {
          instructionString: stringId ?? null,
          instructionText: problem?.[textPath || 'instructionText'] ?? null,
          instructionAudio: problem?.[audioPath || 'instructionAudio'] ?? null,
          multiLocaleInstructions: problem?.multiLocaleInstructions || null,
        },
        commonString: inspectReadingString(strings, stringId, textLocale || effectiveLocale),
        renderedText: React.isValidElement(instruction?.text)
          ? (instruction.text.props?.dangerouslySetInnerHTML?.__html
            ?? instruction.text.props?.children ?? '(React element)')
          : (instruction?.text ?? null),
        chosenAudioFile: instruction?.audio || null,
        chosenAudioPath: instruction?.audioPath || null,
      });
    }

    this.setState(
      { instruction },
      autoPlay ? this.playInstructionsAudio : undefined
    );
  };

  playInstructionsAudio = e => {
    const { activityId, activityAudioBaseUrl } = this.props;
    const { instruction } = this.state;

    if (!instruction || !instruction.audio) return null;

    const audioUrl = (
      instruction.audioPath ||
      activityAudioBaseUrl ||
      `${activitiesURL}/${activityId}/gamedata/`
    ) + instruction.audio;
    toggleAudio(audioUrl);
  };

  render() {
    const { textStyle, className, wrapperStyle } = this.props;
    const { instruction } = this.state;

    if (!instruction || !instruction.text) {
      return null;
    }

    return (
      <InstructionsTextBlock
        wrapperStyle={wrapperStyle}
        text={instruction.text}
        className={className}
        textStyle={textStyle}
      />
    );
  }
}

Instructions.defaultProps = {
  autoPlay: true
};

Instructions.propTypes = {
  autoPlay: PropTypes.bool,
  activityId: PropTypes.string.isRequired,
  activityAudioBaseUrl: PropTypes.string,
  solutionLocale: PropTypes.object,
  solutionLocaleOverride: PropTypes.object,
  instructionTextLocale: PropTypes.string,
  locale: PropTypes.string,
  translate: PropTypes.func.isRequired,
  getStringAudio: PropTypes.func.isRequired,
  textStyle: PropTypes.object,
  problem: PropTypes.object
};

const mapStateToProps = ({
  flinkPlay: { solutionLocale, activityLocale, solutionTranslate },
  status: { translate },
  game: { currentActivity },
  common: { strings }
}) => ({
  solutionLocale: activityLocale || solutionLocale,
  language: solutionLocale?.code || '',
  translate: solutionTranslate || translate,
  strings,
  activityId: currentActivity.activity._id
});

export default compose(
  connect(mapStateToProps),
  withGetStringAudio
)(Instructions);
